import type { CommandItem } from "@replohq/lattice/utils/commands";
import type {
  CommandItemDisplayState,
  DynamicCommandItem,
} from "@v2/hooks/useDynamicCommandMenuItems";

import * as React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { useDebugOptionsCommandItems } from "@v1/hooks/useDebugOptionsCommandItems";
import { adminCommandIcon } from "@v2/features/admin/adminCommandIcon";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import {
  disableFeatureFlag,
  enableFeatureFlag,
  isFeatureEnabled,
} from "@v2/features/infra/featureFlags";
import {
  useDynamicCommandMenuItem,
  useDynamicCommandMenuItems,
} from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { featureFlags } from "replo-utils/lib/featureFlags";

/**
 * Provider component that registers debug and feature flag command menu items.
 * These are global items available in both v1 and v2 editors.
 */
export function DebugCommandMenuItemsProvider() {
  const isDebugMode = useIsDebugMode();
  const { toggle: toggleDebugPanel } = useAdminPanelActions();
  const debugOptionsCommandItems = useDebugOptionsCommandItems();
  // posthog/localStorage feature flags don't have a hook to subscribe to
  // changes, so we bump a counter after a toggle and include it in the memo
  // deps to recompute the children labels and isActive flags. Same pattern as
  // FeatureFlagsTab in the admin panel.
  const [flagToggleCounter, setFlagToggleCounter] = React.useState(0);
  const onFlagToggled = React.useCallback(
    () => setFlagToggleCounter((counter) => counter + 1),
    [],
  );

  // Feature flags submenu
  const featureFlagsItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Feature Flags...",
      description: "Browse feature flag toggles",
      group: "debug" as const,
      icon: adminCommandIcon,
      type: "children" as const,
      children: Object.fromEntries(
        featureFlags.map((flag) => {
          const { value } = flag;
          const isEnabled = isFeatureEnabled(value);
          const child: CommandItem & CommandItemDisplayState = {
            label: `Set ${value} to ${isEnabled ? "off" : "on"}`,
            description: isEnabled ? "On" : "Off",
            isActive: isEnabled,
            icon: {
              type: "custom" as const,
              content: (
                <div className="flex h-[16px] w-[16px] items-center justify-center">
                  <div
                    className={cn(
                      "h-[8px] w-[8px] rounded-full",
                      isEnabled ? "bg-emerald" : "bg-default/30",
                    )}
                  />
                </div>
              ),
            },
            keepMenuOpenOnSelect: true,
            onSelect: () => {
              if (isFeatureEnabled(value)) {
                disableFeatureFlag(value);
              } else {
                enableFeatureFlag(value);
              }
              onFlagToggled();
            },
          };
          return [value, child];
        }),
      ),
    };
  }, [isDebugMode, flagToggleCounter, onFlagToggled]);
  useDynamicCommandMenuItem("debug-feature-flags", featureFlagsItem);

  // Toggle debug panel
  const toggleDebugPanelItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Toggle Debug Panel",
      description: "Open or close the admin debug panel",
      group: "debug" as const,
      icon: adminCommandIcon,
      onSelect: () => {
        toggleDebugPanel();
      },
    };
  }, [isDebugMode, toggleDebugPanel]);
  useDynamicCommandMenuItem("debug-toggle-panel", toggleDebugPanelItem);

  // Debug options
  const debugOptionsItems = React.useMemo(() => {
    if (!isDebugMode) {
      return {};
    }

    const result: Record<string, DynamicCommandItem> = {};

    Object.entries(debugOptionsCommandItems).forEach(([key, item]) => {
      const itemKey = `debug-option-${key}`;
      result[itemKey] = {
        ...item,
        group: "debugOptions",
      };
    });

    return result;
  }, [isDebugMode, debugOptionsCommandItems]);
  useDynamicCommandMenuItems(debugOptionsItems);

  // Clear announcements from local storage
  const clearAnnouncementsItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Clear Announcements",
      description: "Remove dismissed announcement state",
      group: "debug" as const,
      icon: adminCommandIcon,
      onSelect: () => {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("replo.announcements")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => {
          localStorage.removeItem(key);
        });
      },
    };
  }, [isDebugMode]);
  useDynamicCommandMenuItem(
    "debug-clear-announcements",
    clearAnnouncementsItem,
  );

  return null;
}
